import { viewTechDocRouteRef } from '@agilelab/plugin-wb-builder-catalog';
import {
  RELATION_HAS_MAIN_TEMPLATE,
  WitboostTemplate,
} from '@agilelab/plugin-wb-builder-common';
import {
  AutoFixHighIcon,
  createActionsColumn,
  FormatListBulletedOffIcon,
  Severity,
  WbInfoTooltip,
  useOverflowingElement,
  WbFavoriteEntityButton,
  WbTable,
  WbTagsArray,
  WbTruncatedTypographyWrapper,
  WbTooltip,
  WbUnstyledLink,
  WbTabPanel,
  WbMoreOptions,
  WbLink,
} from '@agilelab/plugin-wb-platform';
import {
  DEFAULT_NAMESPACE,
  Entity,
  parseEntityRef,
  RELATION_HAS_PART,
  stringifyEntityRef,
} from '@mainstage/catalog-model';
import { useApi, useRouteRef } from '@mainstage/core-plugin-api';
import {
  ScmIntegrationIcon,
  scmIntegrationsApiRef,
} from '@mainstage/integration-react';
import { catalogLocationCreatePermission } from '@mainstage/plugin-catalog-common/alpha';
import {
  getEntitySourceLocation,
  useRelatedEntities,
} from '@mainstage/plugin-catalog-react';
import { usePermission } from '@mainstage/plugin-permission-react';
import {
  Box,
  IconButton,
  makeStyles,
  MenuItem,
  Typography,
  useTheme,
} from '@material-ui/core';
import FormatListBulleted from '@material-ui/icons/FormatListBulleted';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import { FC, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { selectedBlueprintRouteRef } from '../../routes';
import { useTechDocsDrawer } from '../hooks/useTechDocsDrawer';
import {
  CustomTemplate,
  findDuplicatesCustomTemplates,
  useWitboostCustomTemplates,
} from '../ScaffolderPage/utils';
import { ComponentIcon } from './ComponentIcon';
import { useBlueprintListContext } from './context/useBlueprintListContext';

const useStyles = makeStyles((theme: any) => ({
  avatar: {
    minWidth: '24px',
    width: '24px',
    maxWidth: '24px',
    height: '24px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontSize: '1.125rem',
    pointerEvents: 'none',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
    padding: '2px',
    borderRadius: '8px',
    textTransform: 'uppercase',
  },
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  errorIcon: {
    color: theme.palette.error.main,
  },
}));

const createNameColumnRenderer = ({
  allowed,
  to,
}: {
  allowed?: boolean;
  to?: string;
}) => {
  return (entity: Entity) => {
    const classes = useStyles();
    const getInitials = (title: string) =>
      title
        .split(' ')
        .slice(0, 2)
        .map(s => s.charAt(0));

    const icon: string | undefined = (entity.metadata?.mesh as any)?.icon;
    const title = entity.metadata.title;

    const isLink = allowed && to;

    return (
      <Box style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <WbFavoriteEntityButton entity={entity} />
        <Box className={classes.avatar}>
          {icon && (
            <img
              src={icon}
              alt=""
              onError={event => ((event.target as any).style.display = 'none')}
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          )}
          {!icon && title && (
            <Typography color="primary">{getInitials(title)}</Typography>
          )}
        </Box>
        {isLink ? (
          <WbLink to={to} showTooltip truncate>
            {title}
          </WbLink>
        ) : (
          <WbTruncatedTypographyWrapper
            value={title ?? ''}
            style={{ fontWeight: 700 }}
          />
        )}
      </Box>
    );
  };
};

const DescriptionColumn = (entity: Entity) => {
  return (
    <WbTruncatedTypographyWrapper value={entity.metadata.description || ''} />
  );
};

const ComponentsColumn = (entity: Entity) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const overflows = useOverflowingElement(ref);

  const { entities: mainTemplate } = useRelatedEntities(entity, {
    type: RELATION_HAS_MAIN_TEMPLATE,
  });

  const { entities } = useRelatedEntities(entity, {
    type: RELATION_HAS_PART,
    kind: 'Template',
  });

  const templates = useMemo(
    () => (entities && mainTemplate ? [...mainTemplate, ...entities] : []),
    [entities, mainTemplate],
  );

  return (
    <Box display="flex" alignItems="flex-end" style={{ gap: '8px' }}>
      <div
        ref={ref}
        style={{
          display: 'flex',
          gap: '8px',
          overflow: 'hidden',
          maxWidth: '233px',
        }}
      >
        {templates.map(template => (
          <ComponentIcon entity={template} />
        ))}
      </div>

      {overflows && '...'}
    </Box>
  );
};

const TagsColumn = (entity: Entity) => {
  return (
    <WbTagsArray
      tags={(entity.metadata.tags || []).map(t => ({ tagFQN: t }))}
    />
  );
};

const ActionColumn = ({
  witboostCustomTemplates,
  entity,
  onOpenDocs,
}: {
  witboostCustomTemplates: Record<string, CustomTemplate[]>;
  entity: Entity;
  onOpenDocs?: (entity: Entity) => void;
}) => {
  const theme = useTheme();
  const classes = useStyles();

  const [menuOpen, setMenuOpen] = useState(false);

  const onMenuClose = () => setMenuOpen(false);

  // CODE LINK
  const scmIntegrationsApi = useApi(scmIntegrationsApiRef);
  const sourceLocation = getEntitySourceLocation(entity, scmIntegrationsApi);

  // TECH DOCS LINK
  const viewTechDoc = useRouteRef(viewTechDocRouteRef);
  const viewTechDocsAnnotation =
    entity.metadata.annotations?.['backstage.io/techdocs-ref'];
  const viewTechDocsLink =
    !!viewTechDocsAnnotation &&
    !!viewTechDoc &&
    viewTechDoc({
      namespace: entity.metadata.namespace ?? DEFAULT_NAMESPACE,
      kind: entity.kind,
      name: entity.metadata.name,
    });

  // REVERSE PROVISIONING LINK
  const customTemplates =
    witboostCustomTemplates[
      (
        entity as WitboostTemplate
      ).spec?.steps?.[0]?.input?.values?.useCaseTemplateId?.toLocaleLowerCase()
    ];
  const reverseProvisioningTemplateUrl = customTemplates?.find(
    customTemplate => customTemplate.kind === 'reverseprovisioningtemplate',
  );

  // EDIT TEMPLATE LINK
  const editTemplateUrl = findDuplicatesCustomTemplates(
    'edittemplate',
    customTemplates,
  );

  const hasNoActions = Boolean(
    !sourceLocation &&
      !editTemplateUrl.result &&
      !reverseProvisioningTemplateUrl &&
      !viewTechDocsLink,
  );

  if (hasNoActions) return null;

  return (
    <WbMoreOptions
      open={menuOpen}
      MenuProps={{ onClick: e => e.stopPropagation() }}
      onClick={e => {
        e.stopPropagation();
        setMenuOpen(true);
      }}
      onClose={onMenuClose}
    >
      {sourceLocation && (
        <MenuItem
          className={classes.menuItem}
          onClick={() => {
            window.location.href = sourceLocation.locationTargetUrl;
          }}
        >
          <ScmIntegrationIcon type={sourceLocation.integrationType} />
          Open Repository
        </MenuItem>
      )}
      {editTemplateUrl.result && editTemplateUrl.result.ref && (
        <MenuItem
          onClick={() => {
            window.location.href = editTemplateUrl.result!.ref;
          }}
          className={classes.menuItem}
        >
          <FormatListBulleted fontSize="small" />
          Editor Wizard
        </MenuItem>
      )}
      {reverseProvisioningTemplateUrl && (
        <MenuItem
          onClick={() => {
            window.location.href = reverseProvisioningTemplateUrl.ref;
          }}
          className={classes.menuItem}
        >
          <AutoFixHighIcon />
          Reverse Provisioning
        </MenuItem>
      )}
      {viewTechDocsLink && (
        <MenuItem
          onClick={() => {
            if (onOpenDocs) {
              onOpenDocs(entity);
            }
            onMenuClose();
          }}
          style={{ borderTop: `1px solid ${theme.palette.grey[300]}` }}
          className={classes.menuItem}
        >
          <InfoIcon fontSize="small" />
          Documentation
        </MenuItem>
      )}
    </WbMoreOptions>
  );
};

const ErrorColumn = ({
  witboostCustomTemplates,
  entity,
}: {
  witboostCustomTemplates: Record<string, CustomTemplate[]>;
  entity: Entity;
}) => {
  const { errors } = useBlueprintListContext();
  const classes = useStyles();

  const customTemplates =
    witboostCustomTemplates[
      (
        entity as WitboostTemplate
      ).spec?.steps?.[0].input?.values?.useCaseTemplateId?.toLocaleLowerCase()
    ];

  const editTemplateUrl = findDuplicatesCustomTemplates(
    'edittemplate',
    customTemplates,
  );

  const error = errors?.find(e => e.entity_id === entity.metadata.uid);

  return (
    <>
      {error?.errorMessage && (
        <WbInfoTooltip
          severity={Severity.Error}
          title={error.errorMessage}
          variant="standard"
        />
      )}
      {editTemplateUrl.error && (
        <WbTooltip
          severity={Severity.Error}
          title={editTemplateUrl.errorMessage ?? 'error'}
        >
          <IconButton
            size="small"
            aria-label="Edit"
            className={classes.errorIcon}
            component={WbUnstyledLink}
            to={`/catalog/${
              entity?.metadata.namespace ?? 'default'
            }/${entity?.kind.toLocaleLowerCase()}/${entity?.metadata.name}`}
          >
            <FormatListBulletedOffIcon />
          </IconButton>
        </WbTooltip>
      )}
    </>
  );
};

export const BlueprintTable: FC = () => {
  const navigate = useNavigate();
  const { entities, loading } = useBlueprintListContext();
  const witboostCustomTemplates = useWitboostCustomTemplates(entities);
  const { openDocs, TechDocsDrawer } = useTechDocsDrawer();
  const location = useLocation();
  const knownDataproduct = location.state?.dataproduct;
  const knownDomain = location.state?.domain;

  const { allowed } = usePermission({
    permission: catalogLocationCreatePermission,
  });

  const blueprintRoute = useRouteRef(selectedBlueprintRouteRef);

  const getEntityLink = (entity: Entity) => {
    const { name } = parseEntityRef(stringifyEntityRef(entity));
    const href = blueprintRoute({ blueprintName: name });

    const to =
      knownDomain && knownDataproduct
        ? `${href}?formData=%7B"domain"%3A"${knownDomain}"%2C"dataproduct"%3A"system:${knownDataproduct}"%7D`
        : href;

    return to;
  };

  return (
    <WbTabPanel value="table">
      <WbTable<any>
        components={{
          tableLoader: { loading },
          tableContent: {
            columns: [
              {
                field: 'metadata.title',
                headerName: 'Name',
                fieldRender: entity => {
                  const to = getEntityLink(entity);
                  return createNameColumnRenderer({ allowed, to })(entity);
                },
                sortable: true,
                width: 300,
              },
              {
                field: 'metadata.description',
                headerName: 'Description',
                fieldRender: DescriptionColumn,
                sortable: true,
              },
              {
                field: 'metadata.components',
                headerName: 'Components',
                fieldRender: ComponentsColumn,
                sortable: true,
              },
              {
                field: 'metadata.tags',
                headerName: 'Tags',
                fieldRender: TagsColumn,
              },
              createActionsColumn({
                width: 100,
                renderActions: e => (
                  <div
                    style={{
                      display: 'flex',
                      gap: '8px',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                    }}
                  >
                    <ErrorColumn
                      entity={e}
                      witboostCustomTemplates={witboostCustomTemplates}
                    />
                    <ActionColumn
                      entity={e}
                      witboostCustomTemplates={witboostCustomTemplates}
                      onOpenDocs={openDocs}
                    />
                  </div>
                ),
              }),
            ],
            rows: entities,
            onRowClick: (entity, e) => {
              e.stopPropagation();
              e.preventDefault();
              if (allowed) {
                const to = getEntityLink(entity);

                navigate(to);
              }
            },
          },
        }}
      />
      {TechDocsDrawer}
    </WbTabPanel>
  );
};
